import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Phone, MapPin, IndianRupee, Package, AlertCircle, RefreshCw, LayoutGrid, List } from 'lucide-react';

export default function RetailerTracker() {
  const API_URL = 'https://retailer-tracker.onrender.com/api';
  
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('retailer_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loginForm, setLoginForm] = useState({
    username: '',
    pin: ''
  });
  const [retailers, setRetailers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [activeTab, setActiveTab] = useState('retailers');
  const [trashRetailers, setTrashRetailers] = useState([]);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState({
    username: '',
    pin: '',
    role: 'user'
  });

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    latitude: null,
    longitude: null,
    contact: '',
    currentStock: 0,
    lastRestockDate: '',
    lastRestockQuantity: 0,
    paymentStatus: 'paid',
    pendingAmount: 0,
    orderQty: 0,
    notes: ''
  });
  const [gettingLocation, setGettingLocation] = useState(false);

  // Fetch retailers on mount and set up auto-refresh
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchRetailers();
    fetchUsers();
    if (user?.role === 'admin') {
      fetchTrash();
    }
    const interval = setInterval(fetchRetailers, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [user]);

  const authHeaders = user
    ? {
        'x-username': user.username,
        'x-pin': user.pin
      }
    : {};

  const fetchRetailers = async () => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/retailers`, {
        headers: authHeaders
      });
      if (!response.ok) throw new Error('Failed to fetch retailers');
      const data = await response.json();

      const normalized = (data || []).map(item => ({
        ...item,
        _id: item?._id || item?.id
      }));

      setRetailers(normalized);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching retailers:', err);
      setError('Failed to load data. Check server connection.');
      setLoading(false);
    }
  };

const fetchUsers = async () => {
  if (!user || user.role !== 'admin') return;
  try {
    const response = await fetch(`${API_URL}/users`, {
      headers: authHeaders
    });

    if (!response.ok) return;

    const data = await response.json();
    setUsers(data);
  } catch (err) {
    console.error(err);
  }
};

const fetchTrash = async () => {
  if (!user || user.role !== 'admin') return;

  try {
    const response = await fetch(`${API_URL}/trash`, {
      headers: authHeaders
    });

    if (!response.ok) return;

    const data = await response.json();

    const normalized = (data || []).map(item => ({
      ...item,
      _id: item?._id || item?.id
    }));

    console.log('Trash data:', normalized);

    setTrashRetailers(normalized);
  } catch (err) {
    console.error(err);
  }
};

  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(userForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      setUserForm({
        username: '',
        pin: '',
        role: 'user'
      });

      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;

    try {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }

      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleLogin = async () => {
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...loginForm,
          username: loginForm.username.toLowerCase().trim()
        })
      });
      if (!response.ok) {
        throw new Error('Invalid username or PIN');
      }
      const data = await response.json();
      const loggedUser = {
        username: loginForm.username.toLowerCase().trim(),
        pin: loginForm.pin,
        role: data.user.role
      };
      localStorage.setItem(
        'retailer_user',
        JSON.stringify(loggedUser)
      );
      setUser(loggedUser);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddEdit = async () => {
    if (!formData.name || !formData.location || !formData.contact) {
      alert('Please fill in name, location, and contact');
      return;
    }

    setSyncing(true);
    try {
      if (editingId) {
        const response = await fetch(`${API_URL}/retailers/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Failed to update');
      } else {
        const response = await fetch(`${API_URL}/retailers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify(formData)
        });
        if (!response.ok) throw new Error('Failed to create');
      }
      await fetchRetailers();
      resetForm();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      latitude: null,
      longitude: null,
      contact: '',
      currentStock: 0,
      lastRestockDate: '',
      lastRestockQuantity: 0,
      paymentStatus: 'paid',
      pendingAmount: 0,
      orderQty: 0,
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const captureLocation = () => {
    setGettingLocation(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
            location: `Lat: ${position.coords.latitude.toFixed(4)}, Lng: ${position.coords.longitude.toFixed(4)}`
          });
          setGettingLocation(false);
        },
        (error) => {
          alert('Error getting location: ' + error.message);
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      alert('Geolocation not supported by this browser');
      setGettingLocation(false);
    }
  };

  const handleEdit = (retailer) => {
    setFormData(retailer);
    setEditingId(retailer._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
      if (!id) {
        alert('Retailer ID is missing. Please refresh the page.');
        return;
      }

      if (!window.confirm('Move this retailer to Trash?')) return;
    
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/retailers/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete: ${errorText}`);
      }
      await fetchRetailers();
      if (user?.role === 'admin') {
        await fetchTrash();
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleRestoreRetailer = async (id) => {
    try {
      const response = await fetch(`${API_URL}/trash/${id}/restore`, {
        method: 'PUT',
        headers: authHeaders
      });

      if (!response.ok) throw new Error('Failed to restore retailer');

      await fetchRetailers();
      await fetchTrash();
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!id) {
      alert('Retailer ID is missing. Refresh the page and try again.');
      return;
    }
    if (!window.confirm('Permanently delete this retailer? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/trash/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to permanently delete retailer: ${errorText}`);
      }

      await fetchTrash();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRestock = async (id) => {
    const retailer = retailers.find(r => r._id === id);
    if (!retailer) return;

    const quantity = prompt('Quantity to add to stock:', retailer.lastRestockQuantity || 0);
    if (!quantity) return;

    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/retailers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          ...retailer,
          currentStock: retailer.currentStock + parseInt(quantity),
          lastRestockDate: new Date().toISOString().split('T')[0],
          lastRestockQuantity: parseInt(quantity)
        })
      });
      if (!response.ok) throw new Error('Failed to update stock');
      await fetchRetailers();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePayment = async (id) => {
    const retailer = retailers.find(r => r._id === id);
    if (!retailer || retailer.pendingAmount <= 0) return;

    const payment = prompt(`Pending: ₹${retailer.pendingAmount}. Enter payment amount:`, retailer.pendingAmount);
    if (!payment) return;

    setSyncing(true);
    try {
      const amount = Math.min(parseInt(payment), retailer.pendingAmount);
      const response = await fetch(`${API_URL}/retailers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          ...retailer,
          pendingAmount: retailer.pendingAmount - amount,
          paymentStatus: (retailer.pendingAmount - amount) === 0 ? 'paid' : 'partial'
        })
      });
      if (!response.ok) throw new Error('Failed to update payment');
      await fetchRetailers();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleOrderUpdate = async (id) => {
    const retailer = retailers.find(r => r._id === id);
    if (!retailer) return;

    const qty = prompt('Enter order quantity:', retailer.orderQty || 0);
    if (qty === null) return;

    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/retailers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          orderQty: Number(qty) || 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update order quantity: ${errorText}`);
      }

      await fetchRetailers();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const filteredRetailers = retailers.filter(r => {
    const matchesFilter = filter === 'all' || 
                         (filter === 'pending' && r.pendingAmount > 0) || 
                         (filter === 'low-stock' && r.currentStock < 5) ||
                         (filter === 'orders' && r.orderQty > 0);

    const matchesSearch =
      (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.location || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCreator =
      creatorFilter === 'all' ||
      r.createdBy === creatorFilter;

    return matchesFilter && matchesSearch && matchesCreator;
  });

  const totalPending = retailers.reduce((sum, r) => sum + (r.pendingAmount || 0), 0);
  const lowStockCount = retailers.filter(r => r.currentStock < 5).length;
  const ordersCount = retailers.filter(r => r.orderQty > 0).length;

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#fafaf8',
        fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'white',
          border: '1px solid #e5e5e0',
          borderRadius: '12px',
          padding: '2rem',
          width: '100%',
          maxWidth: '380px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: '#ff6b35',
            fontWeight: 700,
            marginBottom: '0.5rem'
          }}>
            Aahara Astra • Sproutzee
          </div>

          <h1 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#1a1a18'
          }}>
            Retailer Tracker
          </h1>
          <p style={{
            margin: '0.5rem 0 1.5rem 0',
            color: '#666',
            fontSize: '0.95rem'
          }}>
            Sign in to manage shared retailer data.
          </p>

          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) =>
                  setLoginForm({
                    ...loginForm,
                    username: e.target.value.toLowerCase()
                  })
                }
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                autoComplete="username"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e5e0',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                PIN
              </label>
              <input
                type="password"
                inputMode="numeric"
                value={loginForm.pin}
                onChange={(e) => setLoginForm({ ...loginForm, pin: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e5e0',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <button
              onClick={handleLogin}
              style={{
                background: '#ff6b35',
                color: 'white',
                border: 'none',
                padding: '0.85rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#ff5520'}
              onMouseOut={(e) => e.target.style.background = '#ff6b35'}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fafaf8', fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a18 0%, #2d2d2a 100%)',
        color: 'white',
        padding: '2.5rem 2rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ marginBottom: '0.25rem' }}>
                <div style={{
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#E1C16E',
                  fontWeight: 700
                }}>
                  Aahara Astra • Sproutzee
                </div>
              </div>

              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                Retailer Tracker
              </h1>
              <p style={{ margin: '0.5rem 0 0 0', color: '#b0b0aa', fontSize: '0.95rem' }}>
                Sproutzee Retail Distribution Network
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#E1C16E',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                👤 {user?.username}
                {user?.role === 'admin' ? ' (Admin)' : ''}
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem('retailer_user');
                  setUser(null);
                  setRetailers([]);
                  setUsers([]);
                  setTrashRetailers([]);
                  setActiveTab('retailers');
                }}
                style={{
                  background: '#444',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#555'}
                onMouseOut={(e) => e.target.style.background = '#444'}
              >
                Logout
              </button>
              <button
                onClick={fetchRetailers}
                disabled={syncing}
                style={{
                  background: syncing ? '#888' : '#666',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  cursor: syncing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
              >
                <RefreshCw size={18} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} /> 
              </button>
              {activeTab === 'retailers' && (
                <button
                  onClick={() => setShowForm(true)}
                  style={{
                    background: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#ff5520'}
                  onMouseOut={(e) => e.target.style.background = '#ff6b35'}
                >
                  <Plus size={20} /> Add Retailer
                </button>
              )}
            </div>
          </div>

          {/* Status & Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p style={{ margin: 0, color: '#b0b0aa', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Retailers</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.8rem', fontWeight: 700 }}>{retailers.length}</p>
            </div>
            <div style={{ background: totalPending > 0 ? 'rgba(255,107,53,0.15)' : 'rgba(52,211,153,0.15)', padding: '1rem', borderRadius: '8px', border: totalPending > 0 ? '1px solid rgba(255,107,53,0.3)' : '1px solid rgba(52,211,153,0.3)' }}>
              <p style={{ margin: 0, color: totalPending > 0 ? '#ff6b35' : '#10b981', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pending Collection</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.8rem', fontWeight: 700, color: totalPending > 0 ? '#ff6b35' : '#10b981' }}>₹{totalPending.toLocaleString()}</p>
            </div>
            <div style={{ background: lowStockCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(52,211,153,0.15)', padding: '1rem', borderRadius: '8px', border: lowStockCount > 0 ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(52,211,153,0.3)' }}>
              <p style={{ margin: 0, color: lowStockCount > 0 ? '#ef4444' : '#10b981', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Low Stock Alerts</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.8rem', fontWeight: 700, color: lowStockCount > 0 ? '#ef4444' : '#10b981' }}>{lowStockCount}</p>
            </div>
            <div style={{
              background: ordersCount > 0 ? 'rgba(251,146,60,0.15)' : 'rgba(52,211,153,0.15)',
              padding: '1rem',
              borderRadius: '8px',
              border: ordersCount > 0
                ? '1px solid rgba(251,146,60,0.3)'
                : '1px solid rgba(52,211,153,0.3)'
            }}>
              <p style={{
                margin: 0,
                color: ordersCount > 0 ? '#f97316' : '#10b981',
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Pending Orders
              </p>
              <p style={{
                margin: '0.5rem 0 0 0',
                fontSize: '1.8rem',
                fontWeight: 700,
                color: ordersCount > 0 ? '#f97316' : '#10b981'
              }}>
                {ordersCount}
              </p>
            </div>
          </div>

          {/* Connection Status */}
          {error && (
            <div style={{ marginTop: '1rem', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.5)', padding: '1rem', borderRadius: '8px', color: '#fca5a5', fontSize: '0.9rem' }}>
              ⚠️ {error}
            </div>
          )}
          {syncing && (
            <div style={{ marginTop: '1rem', background: 'rgba(96,165,250,0.2)', border: '1px solid rgba(96,165,250,0.5)', padding: '1rem', borderRadius: '8px', color: '#93c5fd', fontSize: '0.9rem' }}>
              🔄 Syncing data across all devices...
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        {/* Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', fontWeight: 700 }}>
                {editingId ? 'Edit Retailer' : 'Add New Retailer'}
              </h2>

              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                    Retailer Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., ABC Supermarket"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e5e0',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                    Location / Address
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <input
                      type="text"
                      placeholder="e.g., Market Street, Delhi or auto-capture GPS"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '1px solid #e5e5e0',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit'
                      }}
                    />
                    <button
                      type="button"
                      onClick={captureLocation}
                      disabled={gettingLocation || syncing}
                      title="Get current GPS location"
                      style={{
                        padding: '0.75rem 1rem',
                        background: gettingLocation ? '#ccc' : '#ff6b35',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: gettingLocation ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => !gettingLocation && (e.target.style.background = '#ff5520')}
                      onMouseOut={(e) => !gettingLocation && (e.target.style.background = '#ff6b35')}
                    >
                      📍 {gettingLocation ? 'Getting...' : 'GPS'}
                    </button>
                  </div>
                  {formData.latitude && formData.longitude && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f0fdf4', borderRadius: '4px', fontSize: '0.8rem', color: '#166534' }}>
                      ✓ Captured: {formData.latitude}, {formData.longitude}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                    Contact Number *
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g., 98765 43210"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e5e0',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                      Current Stock
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.currentStock}
                      onChange={(e) => setFormData({ ...formData, currentStock: parseInt(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e5e5e0',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                      Pending Payment (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pendingAmount}
                      onChange={(e) => setFormData({ ...formData, pendingAmount: parseInt(e.target.value) || 0, paymentStatus: parseInt(e.target.value) > 0 ? 'pending' : 'paid' })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e5e5e0',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#1a1a18'
                  }}>
                    Order Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.orderQty || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orderQty: parseInt(e.target.value) || 0
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e5e0',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: '#1a1a18' }}>
                    Notes
                  </label>
                  <textarea
                    placeholder="Any additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e5e0',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      minHeight: '80px',
                      resize: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  onClick={handleAddEdit}
                  disabled={syncing}
                  style={{
                    flex: 1,
                    background: syncing ? '#ccc' : '#ff6b35',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !syncing && (e.target.style.background = '#ff5520')}
                  onMouseOut={(e) => !syncing && (e.target.style.background = '#ff6b35')}
                >
                  {syncing ? 'Saving...' : (editingId ? 'Update' : 'Add')} Retailer
                </button>
                <button
                  onClick={resetForm}
                  disabled={syncing}
                  style={{
                    flex: 1,
                    background: '#e5e5e0',
                    color: '#1a1a18',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    cursor: syncing ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => !syncing && (e.target.style.background = '#d5d5d0')}
                  onMouseOut={(e) => !syncing && (e.target.style.background = '#e5e5e0')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}


        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setActiveTab('retailers')}
            style={{
              padding: '0.75rem 1.25rem',
              border: '1px solid #e5e5e0',
              borderRadius: '6px',
              background: activeTab === 'retailers' ? '#ff6b35' : 'white',
              color: activeTab === 'retailers' ? 'white' : '#1a1a18',
              cursor: 'pointer'
            }}
          >
            Retailers
          </button>

          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '0.75rem 1.25rem',
                border: '1px solid #e5e5e0',
                borderRadius: '6px',
                background: activeTab === 'users' ? '#ff6b35' : 'white',
                color: activeTab === 'users' ? 'white' : '#1a1a18',
                cursor: 'pointer'
              }}
            >
              Users
            </button>
          )}
          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('trash')}
              style={{
                padding: '0.75rem 1.25rem',
                border: '1px solid #e5e5e0',
                borderRadius: '6px',
                background: activeTab === 'trash' ? '#ff6b35' : 'white',
                color: activeTab === 'trash' ? 'white' : '#1a1a18',
                cursor: 'pointer'
              }}
            >
              Trash ({trashRetailers.length})
            </button>
          )}
        </div>
        {user?.role === 'admin' && activeTab === 'trash' && (
          <div
            style={{
              background: 'white',
              border: '1px solid #e5e5e0',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '2rem'
            }}
          >
            <div
              style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #e5e5e0',
                background: '#fafaf8'
              }}
            >
              <h2 style={{ margin: 0 }}>Trash Management</h2>
              <p style={{ margin: '0.25rem 0 0 0', color: '#666' }}>
                Deleted retailers can be restored or permanently removed.
              </p>
            </div>

            {trashRetailers.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                No deleted retailers.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 2fr 1.3fr 1fr 1fr 1.5fr',
                    gap: '1rem',
                    padding: '1rem 1.5rem',
                    background: '#f8f9fa',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    borderBottom: '1px solid #e5e5e0'
                  }}
                >
                  <div>Retailer</div>
                  <div>Location</div>
                  <div>Contact</div>
                  <div>Created By</div>
                  <div>Deleted By</div>
                  <div>Actions</div>
                </div>

                {trashRetailers.map((retailer) => (
                  <div
                    key={retailer._id || retailer.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1.3fr 1fr 1fr 1.5fr',
                      gap: '1rem',
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong>{retailer.name}</strong>
                      {retailer.deletedAt && (
                        <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                          {new Date(retailer.deletedAt).toLocaleString()}
                        </div>
                      )}
                    </div>

                    <div>{retailer.location || '-'}</div>

                    <div>
                      {retailer.contact ? (
                        <a href={`tel:${retailer.contact}`} style={{ color: '#ff6b35' }}>
                          {retailer.contact}
                        </a>
                      ) : '-'}
                    </div>

                    <div>{retailer.createdBy || '-'}</div>
                    <div>{retailer.deletedBy || '-'}</div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleRestoreRetailer(retailer._id || retailer.id)}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '0.45rem 0.75rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Restore
                      </button>

                      <button
                        onClick={() => handlePermanentDelete(retailer._id || retailer.id)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '0.45rem 0.75rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {user?.role === 'admin' && activeTab === 'users' && (
          <div style={{
            background: 'white',
            border: '1px solid #e5e5e0',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h2 style={{ marginTop: 0 }}>Users</h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr auto',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <input
                placeholder='Username'
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
              />

              <input
                placeholder='PIN'
                value={userForm.pin}
                onChange={(e) => setUserForm({ ...userForm, pin: e.target.value })}
              />

              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value='user'>User</option>
                <option value='admin'>Admin</option>
              </select>

              <button
                onClick={handleCreateUser}
                disabled={!userForm.username || !userForm.pin}
              >
                Create
              </button>
            </div>

            {users.map((u) => (
              <div
                key={u._id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid #eee'
                }}
              >
                <div>
                  <strong>{u.username}</strong> ({u.role})
                </div>

                <button onClick={() => handleDeleteUser(u._id)}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'retailers' && (
          <>
    {/* Filters */}

    <div style={{
      background: 'white',
      border: '1px solid #e5e5e0',
      borderRadius: '12px',
      padding: '1rem',
      marginBottom: '1.5rem'
    }}>
      <div style={{
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="🔍 Search retailer or location"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: '250px',
            padding: '0.85rem',
            border: '1px solid #e5e5e0',
            borderRadius: '8px'
          }}
        />

        <div style={{ minWidth: '220px' }}>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.35rem'
            }}
          >
            👤 Filter by User
          </div>

          <select
            value={creatorFilter}
            onChange={(e) => setCreatorFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.85rem',
              border: creatorFilter !== 'all' ? '2px solid #ff6b35' : '1px solid #e5e5e0',
              borderRadius: '10px',
              background: creatorFilter !== 'all' ? '#fff7ed' : 'white',
              fontWeight: 600,
              color: '#1f2937'
            }}
          >
            <option value="all">👥 All Users</option>
            {[...new Set(retailers.map(r => r.createdBy).filter(Boolean))].map((creator) => {
              const count = retailers.filter(r => r.createdBy === creator).length;
              return (
                <option key={creator} value={creator}>
                  {creator} ({count})
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '0.35rem'
            }}
          >
            View
          </div>

          <div
            style={{
              display: 'flex',
              border: '1px solid #e5e5e0',
              borderRadius: '10px',
              overflow: 'hidden'
            }}
          >
            <button
              onClick={() => setViewMode('grid')}
              style={{
                background: viewMode === 'grid' ? '#ff6b35' : 'white',
                color: viewMode === 'grid' ? 'white' : '#374151',
                border: 'none',
                padding: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LayoutGrid size={18} />
            </button>

            <button
              onClick={() => setViewMode('list')}
              style={{
                background: viewMode === 'list' ? '#ff6b35' : 'white',
                color: viewMode === 'list' ? 'white' : '#374151',
                border: 'none',
                padding: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        marginTop: '1rem'
      }}>
        {[
          { key: 'all', label: `All (${retailers.length})` },
          { key: 'pending', label: `Pending (${retailers.filter(r => r.pendingAmount > 0).length})` },
          { key: 'low-stock', label: `Low Stock (${lowStockCount})` },
          { key: 'orders', label: `Orders (${ordersCount})` }
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            style={{
              padding: '0.65rem 1rem',
              borderRadius: '999px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              background: filter === item.key ? '#ff6b35' : '#f3f4f6',
              color: filter === item.key ? 'white' : '#374151'
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <p style={{ color: '#666', fontSize: '0.95rem' }}>Loading retailers...</p>
          </div>
        )}

        {/* Retailers Grid */}
        {!loading && filteredRetailers.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e5e0'
          }}>
            <AlertCircle size={48} style={{ color: '#d4d4ce', margin: '0 auto 1rem' }} />
            <p style={{ color: '#8b8b87', fontSize: '0.95rem' }}>
              {retailers.length === 0 ? 'No retailers added yet. Click "Add Retailer" to get started.' : 'No retailers match your filters.'}
            </p>
          </div>
        ) : (
          <div
            style={
              viewMode === 'grid'
                ? {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                    gap: '1.5rem',
                    alignItems: 'start'
                  }
                : {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                  }
            }
          >
            {filteredRetailers.map(retailer => (
              <div key={retailer._id || retailer.id} style={{
                background: 'white',
                width: '100%',
                boxSizing: 'border-box',
                alignSelf: 'start',
                height: 'fit-content',
                borderRadius: '12px',
                border: '1px solid #e5e5e0',
                padding: '1.5rem',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.2s',
                ...(viewMode === 'list' ? {
                  padding: '0.85rem 1rem',
                  display: 'grid',
                  gridTemplateColumns: '2fr 2fr 1fr 1fr auto',
                  alignItems: 'center',
                  gap: '0.75rem'
                } : {
                  display: 'block'
                })
              }}>
                {viewMode === 'list' ? (
                  <>
                    <div>
                      <strong style={{ fontSize: '1rem' }}>{retailer.name}</strong>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {retailer.createdBy || '-'}
                      </div>
                    </div>

                    <div>
                      <div>{retailer.location}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {retailer.contact}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 700 }}>
                        Stock: {retailer.currentStock}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: retailer.orderQty > 0 ? '#ea580c' : '#666' }}>
                        Order: {retailer.orderQty || 0}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontWeight: 700, color: retailer.pendingAmount > 0 ? '#ff6b35' : '#10b981' }}>
                        ₹{retailer.pendingAmount}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>Pending</div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleOrderUpdate(retailer._id || retailer.id)}
                        style={{
                          padding: '0.35rem 0.55rem',
                          fontSize: '0.75rem'
                        }}
                      >
                        Order
                      </button>
                      <button
                        onClick={() => handleRestock(retailer._id || retailer.id)}
                        style={{
                          padding: '0.35rem 0.55rem',
                          fontSize: '0.75rem'
                        }}
                      >
                        Stock
                      </button>
                      <button
                        onClick={() => handlePayment(retailer._id || retailer.id)}
                        style={{
                          padding: '0.35rem 0.55rem',
                          fontSize: '0.75rem'
                        }}
                      >
                        Pay
                      </button>
                      {(user?.role === 'admin' || retailer.createdBy === user?.username) && (
                        <button
                          onClick={() => handleEdit(retailer)}
                          style={{
                            padding: '0.35rem 0.55rem',
                            fontSize: '0.75rem'
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                <>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1a1a18' }}>{retailer.name}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {(user?.role === 'admin' || retailer.createdBy === user?.username) && (
                        <button
                          onClick={() => handleEdit(retailer)}
                          disabled={syncing}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: syncing ? 'not-allowed' : 'pointer',
                            color: '#666',
                            padding: '0.5rem',
                            display: 'flex',
                            opacity: syncing ? 0.5 : 1
                          }}
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      {(user?.role === 'admin' || retailer.createdBy === user?.username) && (
                        <button
                          onClick={() => handleDelete(retailer._id || retailer.id)}
                          disabled={syncing}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: syncing ? 'not-allowed' : 'pointer',
                            color: '#ef4444',
                            padding: '0.5rem',
                            display: 'flex',
                            opacity: syncing ? 0.5 : 1
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#555' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={16} style={{ color: '#999', flexShrink: 0 }} />
                      <div>
                        {retailer.latitude && retailer.longitude ? (
                          <a
                            href={`https://www.google.com/maps?q=${retailer.latitude},${retailer.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#ff6b35',
                              textDecoration: 'none',
                              fontWeight: 500
                            }}
                          >
                            {retailer.location}
                            <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                              📍 {retailer.latitude}, {retailer.longitude} (Open Map)
                            </div>
                          </a>
                        ) : (
                          <span>{retailer.location}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Phone size={16} style={{ color: '#999', flexShrink: 0 }} />
                      <a href={`tel:${retailer.contact}`} style={{ color: '#ff6b35', textDecoration: 'none' }}>{retailer.contact}</a>
                    </div>
                    {retailer.createdBy && (
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: '#666',
                          paddingLeft: '1.5rem',
                          marginTop: '0.25rem'
                        }}
                      >
                        Created By: <strong>{retailer.createdBy}</strong>
                      </div>
                    )}
                  </div>

                  {/* Stock & Payment Status */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ background: retailer.currentStock < 5 ? '#fef2f2' : '#f8faf7', padding: '0.75rem', borderRadius: '8px', border: retailer.currentStock < 5 ? '1px solid #fee2e2' : '1px solid #e5e5e0' }}>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 }}>Stock</p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: retailer.currentStock < 5 ? '#ef4444' : '#1a1a18' }}>{retailer.currentStock}</p>
                    </div>
                    <div style={{ background: retailer.pendingAmount > 0 ? '#fef3f2' : '#f8faf7', padding: '0.75rem', borderRadius: '8px', border: retailer.pendingAmount > 0 ? '1px solid #fed7d3' : '1px solid #e5e5e0' }}>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 }}>Pending</p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: retailer.pendingAmount > 0 ? '#ff6b35' : '#10b981' }}>₹{retailer.pendingAmount}</p>
                    </div>
                  </div>

                  {retailer.orderQty > 0 && (
                    <div style={{
                      background: '#fff7ed',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #fed7aa',
                      marginBottom: '1rem'
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        color: '#9a3412',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        fontWeight: 600
                      }}>
                        Order Required
                      </p>
                      <p style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: '#ea580c'
                      }}>
                        {retailer.orderQty}
                      </p>
                    </div>
                  )}

                  {retailer.notes && (
                    <div style={{ background: '#f8faf7', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>{retailer.notes}</p>
                    </div>
                  )}

                  {retailer.lastRestockDate && (
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: '#999' }}>
                      Last restock: {retailer.lastRestockDate}
                    </p>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                    <button
                      onClick={() => handleRestock(retailer._id || retailer.id)}
                      disabled={syncing}
                      style={{
                        background: '#f0f0eb',
                        border: '1px solid #e5e5e0',
                        color: '#1a1a18',
                        padding: '0.65rem',
                        borderRadius: '6px',
                        cursor: syncing ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        transition: 'all 0.2s',
                        opacity: syncing ? 0.6 : 1
                      }}
                      onMouseOver={(e) => !syncing && (e.target.style.background = '#e5e5e0')}
                      onMouseOut={(e) => !syncing && (e.target.style.background = '#f0f0eb')}
                    >
                      <Package size={14} /> Restock
                    </button>
                    <button
                      onClick={() => handleOrderUpdate(retailer._id || retailer.id)}
                      disabled={syncing}
                      style={{
                        background: retailer.orderQty > 0 ? '#fff7ed' : '#f8faf7',
                        border: retailer.orderQty > 0 ? '1px solid #fed7aa' : '1px solid #e5e5e0',
                        color: retailer.orderQty > 0 ? '#ea580c' : '#1a1a18',
                        padding: '0.65rem',
                        borderRadius: '6px',
                        cursor: syncing ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        opacity: syncing ? 0.6 : 1
                      }}
                    >
                      Order ({retailer.orderQty || 0})
                    </button>
                    <button
                      onClick={() => handlePayment(retailer._id || retailer.id)}
                      disabled={retailer.pendingAmount === 0 || syncing}
                      style={{
                        background: retailer.pendingAmount > 0 && !syncing ? '#ff6b35' : '#d4d4ce',
                        border: 'none',
                        color: 'white',
                        padding: '0.65rem',
                        borderRadius: '6px',
                        cursor: (retailer.pendingAmount > 0 && !syncing) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => (retailer.pendingAmount > 0 && !syncing) && (e.target.style.background = '#ff5520')}
                      onMouseOut={(e) => (retailer.pendingAmount > 0 && !syncing) && (e.target.style.background = '#ff6b35')}
                    >
                      <IndianRupee size={14} /> Payment
                    </button>
                  </div>
                </>
                )}
              </div>
            ))}
          </div>
        )}

        </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
